import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useState } from 'react';
import { api } from '@/lib/api';
import { useToast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { Contract, FeeType, ContractDate, ContractDateLog, LedgerEntry, PilloryPost, Review, PaginatedResponse, PaymentSkipRequest } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ExternalLink, Target, Clock, ThumbsUp, Star, Download } from 'lucide-react';
import { ProfileLink } from '@/components/ProfileLink';
import { BalanceChart } from '@/components/BalanceChart';
import { PaymentTimeline } from '@/components/PaymentTimeline';
import { WheelOfFortune } from '@/components/games/WheelOfFortune';
import { DiceRoll } from '@/components/games/DiceRoll';
import { ContractTasks } from '@/components/games/ContractTasks';
import { PaymentSkip } from '@/components/PaymentSkip';
import { ShareLinks } from '@/components/ShareLinks';
import { trackEvent } from '@/lib/faro';
import { downloadContractWorkbook, type ContractExportPayload } from '@/lib/contractWorkbook';

const LINK_LABELS: Record<string, string> = {
    cashapp: 'Cash App',
    venmo: 'Venmo',
    paypal: 'PayPal',
    twitter: 'Twitter / X',
    amazon: 'Amazon Wishlist',
    lovense: 'Lovense Wishlist',
    throne: 'Throne',
    onlyfans: 'OnlyFans',
    other: 'Other',
};

export default function ContractDetailPage() {
    const { id } = useParams();
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentError, setPaymentError] = useState('');
    const [feeForm, setFeeForm] = useState({ description: '', amount: '', type: 'fee' });
    const [feeError, setFeeError] = useState('');
    const [newTrackerLabel, setNewTrackerLabel] = useState('');
    const [editingTracker, setEditingTracker] = useState<{ id: number; date: string } | null>(null);
    const [historyTrackerId, setHistoryTrackerId] = useState<number | null>(null);
    const [pilloryForm, setPilloryForm] = useState({ reason: '', fee_per_vote: '', duration_hours: '24', max_votes_per_user: '1', max_total_votes: '', require_login: true });
    const [pilloryError, setPilloryError] = useState('');
    const [reviewForm, setReviewForm] = useState({ rating: 5, body: '' });
    const [signature, setSignature] = useState('');
    const [reviewError, setReviewError] = useState('');

    const { data: contractData, isLoading } = useQuery({
        queryKey: ['contract', id],
        queryFn: () => api.get<{ data: Contract }>(`/contracts/${id}`),
    });

    const { data: ledgerData } = useQuery({
        queryKey: ['ledger', id],
        queryFn: () => api.get<PaginatedResponse<LedgerEntry>>(`/contracts/${id}/ledger`),
    });

    const { data: reviewData } = useQuery({
        queryKey: ['contract-review', id],
        queryFn: () => api.get<{ data: Review | null }>(`/contracts/${id}/review`),
    });

    const reviewMutation = useMutation({
        mutationFn: (data: { contract_id: number; rating: number; body: string }) =>
            api.post('/reviews', data),
        onSuccess: () => {
            trackEvent('review_submitted', { contract_id: Number(id) });
            setReviewError('');
            queryClient.invalidateQueries({ queryKey: ['contract-review', id] });
        },
        onError: (err: Error) => setReviewError(err.message),
    });

    const paymentMutation = useMutation({
        mutationFn: (amount: number) =>
            api.post(`/contracts/${id}/payment`, { amount }),
        onSuccess: () => {
            trackEvent('payment_made', { contract_id: Number(id), amount: Number(paymentAmount) });
            setPaymentAmount('');
            setPaymentError('');
            toast('Payment recorded.', 'success');
            queryClient.invalidateQueries({ queryKey: ['contract', id] });
            queryClient.invalidateQueries({ queryKey: ['ledger', id] });
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
        },
        onError: (err: Error) => setPaymentError(err.message),
    });

    const predefinedFeeMutation = useMutation({
        mutationFn: (feeTypeId: number) =>
            api.post(`/contracts/${id}/fee/${feeTypeId}`, {}),
        onSuccess: () => {
            trackEvent('fee_applied', { contract_id: Number(id), type: 'predefined' });
            toast('Fee applied successfully.', 'success');
            queryClient.invalidateQueries({ queryKey: ['contract', id] });
            queryClient.invalidateQueries({ queryKey: ['ledger', id] });
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
        },
        onError: (err: Error) => toast(err.message),
    });

    const feeMutation = useMutation({
        mutationFn: (data: { description: string; amount: number; type: string }) =>
            api.post(`/contracts/${id}/fee`, data),
        onSuccess: () => {
            trackEvent('fee_applied', { contract_id: Number(id), type: 'ad_hoc' });
            setFeeForm({ description: '', amount: '', type: 'fee' });
            setFeeError('');
            queryClient.invalidateQueries({ queryKey: ['contract', id] });
            queryClient.invalidateQueries({ queryKey: ['ledger', id] });
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
        },
        onError: (err: Error) => setFeeError(err.message),
    });

    const navigate = useNavigate();
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    const closeMutation = useMutation({
        mutationFn: () => api.post(`/contracts/${id}/close`, {}),
        onSuccess: () => {
            trackEvent('contract_closed', { contract_id: Number(id) });
            setShowCloseConfirm(false);
            toast('Contract closed.', 'success');
            queryClient.invalidateQueries({ queryKey: ['contract', id] });
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
        },
        onError: (err: Error) => toast(err.message),
    });

    const cancelMutation = useMutation({
        mutationFn: () => api.post(`/contracts/${id}/cancel`, {}),
        onSuccess: () => {
            trackEvent('contract_cancelled', { contract_id: Number(id) });
            setShowCancelConfirm(false);
            toast('Contract cancelled.', 'success');
            queryClient.invalidateQueries({ queryKey: ['contract', id] });
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
        },
        onError: (err: Error) => toast(err.message),
    });

    const recallMutation = useMutation({
        mutationFn: () => api.post(`/contracts/${id}/recall`, {}),
        onSuccess: () => {
            trackEvent('contract_recalled', { contract_id: Number(id) });
            toast('Contract pulled back. You can now edit it.', 'success');
            queryClient.invalidateQueries({ queryKey: ['contract', id] });
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
            navigate(`/contracts/${id}/edit`);
        },
        onError: (err: Error) => toast(err.message),
    });

    const resendMutation = useMutation({
        mutationFn: () => api.post(`/contracts/${id}/resend`, {}),
        onSuccess: () => {
            trackEvent('contract_resent', { contract_id: Number(id) });
            toast('Contract sent to sub for signing.', 'success');
            queryClient.invalidateQueries({ queryKey: ['contract', id] });
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
        },
        onError: (err: Error) => toast(err.message),
    });

    const signMutation = useMutation({
        mutationFn: (sig: string) => api.post(`/contracts/${id}/sign`, { signature: sig }),
        onSuccess: () => {
            trackEvent('contract_signed', { contract_id: Number(id) });
            toast('Contract signed and activated!', 'success');
            setSignature('');
            queryClient.invalidateQueries({ queryKey: ['contract', id] });
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
        },
        onError: (err: Error) => toast(err.message),
    });

    const addTrackerMutation = useMutation({
        mutationFn: (label: string) =>
            api.post(`/contracts/${id}/trackers`, { label }),
        onSuccess: () => {
            setNewTrackerLabel('');
            queryClient.invalidateQueries({ queryKey: ['contract', id] });
        },
        onError: (err: Error) => toast(err.message),
    });

    const updateTrackerMutation = useMutation({
        mutationFn: ({ trackerId, tracked_date }: { trackerId: number; tracked_date: string }) =>
            api.put(`/contracts/${id}/trackers/${trackerId}`, { tracked_date }),
        onSuccess: (_data, variables) => {
            setEditingTracker(null);
            queryClient.invalidateQueries({ queryKey: ['contract', id] });
            queryClient.invalidateQueries({ queryKey: ['tracker-history', id, variables.trackerId] });
        },
        onError: (err: Error) => toast(err.message),
    });

    const deleteTrackerMutation = useMutation({
        mutationFn: (trackerId: number) =>
            api.delete(`/contracts/${id}/trackers/${trackerId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contract', id] });
        },
        onError: (err: Error) => toast(err.message),
    });

    const { data: historyData } = useQuery({
        queryKey: ['tracker-history', id, historyTrackerId],
        queryFn: () => api.get<{ data: ContractDateLog[] }>(`/contracts/${id}/trackers/${historyTrackerId}/history`),
        enabled: historyTrackerId !== null,
    });

    const { data: pilloryData } = useQuery({
        queryKey: ['contract-pillory', id],
        queryFn: () => api.get<{ data: PilloryPost[] }>(`/contracts/${id}/pillory`),
    });

    const pilloryMutation = useMutation({
        mutationFn: (data: Record<string, unknown>) =>
            api.post('/pillory', data),
        onSuccess: () => {
            trackEvent('pillory_post_created', { contract_id: Number(id) });
            setPilloryForm({ reason: '', fee_per_vote: '', duration_hours: '24', max_votes_per_user: '1', max_total_votes: '', require_login: true });
            setPilloryError('');
            queryClient.invalidateQueries({ queryKey: ['contract-pillory', id] });
        },
        onError: (err: Error) => setPilloryError(err.message),
    });

    const pilloryVoteMutation = useMutation({
        mutationFn: (postId: number) =>
            api.post(`/pillory/${postId}/vote`, {}),
        onSuccess: () => {
            trackEvent('pillory_voted', { contract_id: Number(id) });
            toast('Vote recorded!', 'success');
            queryClient.invalidateQueries({ queryKey: ['contract-pillory', id] });
            queryClient.invalidateQueries({ queryKey: ['contract', id] });
            queryClient.invalidateQueries({ queryKey: ['ledger', id] });
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
        },
        onError: (err: Error) => toast(err.message),
    });

    const contract = contractData?.data;
    const entries = ledgerData?.data ?? [];

    if (isLoading) return <p className="text-sm text-slate-400">Loading...</p>;
    if (!contract) return <p className="text-sm text-red-400">Contract not found.</p>;

    const isSub = user?.id === contract.sub?.id;
    const isDom = user?.id === contract.dom?.id;
    const isParticipant = isDom || isSub;
    const isActive = contract.status === 'active';
    const isPending = contract.status === 'pending';
    const isDraft = contract.status === 'draft';
    const isUnsigned = isPending || isDraft;

    return (
        <div className="space-y-6 pb-20">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-xl font-semibold">{contract.title}</h1>
                    <p className="text-sm text-slate-400">
                        {isParticipant ? (
                            isDom
                                ? <>Sub: <ProfileLink userId={contract.sub!.id} displayName={contract.sub!.display_name} /></>
                                : <>Dom: <ProfileLink userId={contract.dom!.id} displayName={contract.dom!.display_name} /></>
                        ) : (
                            <>
                                <ProfileLink userId={contract.dom!.id} displayName={contract.dom!.display_name} />
                                {' & '}
                                <ProfileLink userId={contract.sub!.id} displayName={contract.sub!.display_name} />
                            </>
                        )}
                    </p>
                </div>
                <Badge
                    variant={contract.status === 'active' ? 'success' : 'secondary'}
                    className={contract.status === 'pending' ? 'bg-amber-500/20 text-amber-400' : ''}
                >
                    {contract.status}
                </Badge>
            </div>

            {/* Draft banner — dom has pulled the contract back to edit */}
            {isParticipant && isSub && isDraft && (
                <Card className="border-slate-700 bg-slate-900/40">
                    <CardContent className="p-4">
                        <p className="text-sm text-slate-300">
                            {contract.dom?.display_name} has pulled this contract back to make edits. You'll be able to sign it again once they resend it.
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Signing banner for pending bespoke contracts */}
            {isParticipant && isSub && isPending && (
                <Card className="border-amber-900/50 bg-amber-950/20">
                    <CardContent className="p-4 space-y-3">
                        <p className="text-sm text-amber-300">
                            This contract is awaiting your signature. Review the terms below, then type your name to sign.
                        </p>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (signature.trim().length >= 2) signMutation.mutate(signature.trim());
                            }}
                            className="flex items-center gap-2"
                        >
                            <Input
                                value={signature}
                                onChange={(e) => setSignature(e.target.value)}
                                placeholder="Type your name to sign..."
                                className="flex-1"
                                required
                                minLength={2}
                            />
                            <Button
                                type="submit"
                                disabled={signMutation.isPending || signature.trim().length < 2}
                                className="bg-amber-600 hover:bg-amber-700"
                            >
                                {signMutation.isPending ? 'Signing...' : 'Sign Contract'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            )}

            {isParticipant && isDom && isPending && (
                <Card className="border-slate-700">
                    <CardContent className="p-4 space-y-3">
                        <p className="text-sm text-slate-400">
                            This contract is pending — waiting for {contract.sub?.display_name} to sign. You can pull it back to make edits before they sign.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => recallMutation.mutate()}
                                disabled={recallMutation.isPending}
                            >
                                {recallMutation.isPending ? 'Pulling back...' : 'Pull Back & Edit'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {isParticipant && isDom && isDraft && (
                <Card className="border-amber-900/50 bg-amber-950/20">
                    <CardContent className="p-4 space-y-3">
                        <p className="text-sm text-amber-300">
                            This contract is being edited and cannot be signed until you resend it to {contract.sub?.display_name}.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <Link to={`/contracts/${contract.id}/edit`}>
                                <Button size="sm" variant="outline">Edit</Button>
                            </Link>
                            <Button
                                size="sm"
                                onClick={() => resendMutation.mutate()}
                                disabled={resendMutation.isPending}
                                className="bg-amber-600 hover:bg-amber-700"
                            >
                                {resendMutation.isPending ? 'Sending...' : 'Resend to Sub'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Action buttons (participants only) */}
            {isParticipant && (
                <>
                    <div className="flex flex-wrap justify-end gap-2">
                        {isDom && isActive && (
                            <Link to={`/contracts/${contract.id}/edit`}>
                                <Button variant="outline" size="sm">Edit</Button>
                            </Link>
                        )}
                        <Link to={`/contracts/${contract.id}/schedule`}>
                            <Button variant="outline" size="sm">Debt Schedule</Button>
                        </Link>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                                const data = await api.get<ContractExportPayload>(`/contracts/${contract.id}/export`);

                                downloadContractWorkbook(data, contract.id);
                            }}
                        >
                            <Download className="h-4 w-4 mr-1" />
                            Export
                        </Button>
                        {isActive && (isDom || (isSub && !(contract.hardcore_mode && !contract.sub_has_revoked_hardcore))) && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                                onClick={() => setShowCloseConfirm(true)}
                            >
                                {isDom ? 'Close Contract' : 'Abandon Contract'}
                            </Button>
                        )}
                        {isUnsigned && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                                onClick={() => setShowCancelConfirm(true)}
                            >
                                Cancel Contract
                            </Button>
                        )}
                    </div>

                    <ConfirmDialog
                        open={showCloseConfirm}
                        onOpenChange={setShowCloseConfirm}
                        title={isDom ? 'Close Contract' : 'Abandon Contract'}
                        description={isDom
                            ? 'This will close the contract and write off the remaining balance. This action cannot be undone.'
                            : 'This will abandon the contract. An abandonment penalty may be applied. This action cannot be undone.'}
                        confirmLabel={isDom ? 'Close Contract' : 'Abandon Contract'}
                        variant="destructive"
                        loading={closeMutation.isPending}
                        onConfirm={() => closeMutation.mutate()}
                    />

                    <ConfirmDialog
                        open={showCancelConfirm}
                        onOpenChange={setShowCancelConfirm}
                        title="Cancel Contract"
                        description="This contract has not been signed yet. Cancelling will withdraw it permanently. This action cannot be undone."
                        confirmLabel="Cancel Contract"
                        variant="destructive"
                        loading={cancelMutation.isPending}
                        onConfirm={() => cancelMutation.mutate()}
                    />
                </>
            )}

            {/* Balance card */}
            <Card>
                <CardContent className="p-4 text-center">
                    <p className="text-xs text-slate-400">Current Balance</p>
                    <p className="text-3xl font-bold text-red-400">
                        {formatCurrency(contract.current_balance)}
                    </p>
                    {contract.next_payment_due_date && isActive && (
                        <p className="mt-1 text-xs text-slate-500">
                            Next payment due: {formatDate(contract.next_payment_due_date)}
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                    { label: 'Total Paid', value: contract.total_paid, color: 'text-emerald-400' },
                    { label: 'Interest', value: contract.total_interest_charged, color: 'text-red-400' },
                    { label: 'Fees', value: contract.total_fees_charged, color: 'text-orange-400' },
                    { label: 'Taxes', value: contract.total_taxes_assessed, color: 'text-purple-400' },
                ].map((stat) => (
                    <Card key={stat.label}>
                        <CardContent className="p-3 text-center">
                            <p className="text-[10px] text-slate-400">{stat.label}</p>
                            <p className={`text-sm font-semibold ${stat.color}`}>
                                {formatCurrency(stat.value)}
                            </p>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Contract Terms + Links row */}
            <div className="grid gap-6 lg:grid-cols-2">
                {/* Contract Terms */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Contract Terms</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                            <div>
                                <dt className="text-[10px] uppercase tracking-wide text-slate-500">Opening Balance</dt>
                                <dd className="font-medium">{formatCurrency(contract.opening_balance)}</dd>
                            </div>
                            <div>
                                <dt className="text-[10px] uppercase tracking-wide text-slate-500">Origination Fee</dt>
                                <dd className="font-medium">{formatCurrency(contract.origination_fee)}</dd>
                            </div>
                            <div>
                                <dt className="text-[10px] uppercase tracking-wide text-slate-500">Interest Rate</dt>
                                <dd className="font-medium">{parseFloat(contract.interest_rate)}% APR</dd>
                            </div>
                            <div>
                                <dt className="text-[10px] uppercase tracking-wide text-slate-500">Compounding</dt>
                                <dd className="font-medium capitalize">{contract.compounding_frequency}</dd>
                            </div>
                            <div>
                                <dt className="text-[10px] uppercase tracking-wide text-slate-500">Min Payment</dt>
                                <dd className="font-medium">{formatCurrency(contract.minimum_payment)}</dd>
                            </div>
                            <div>
                                <dt className="text-[10px] uppercase tracking-wide text-slate-500">Max Payment</dt>
                                <dd className="font-medium">{contract.maximum_payment ? formatCurrency(contract.maximum_payment) : 'None'}</dd>
                            </div>
                            {contract.payment_due_day !== undefined && contract.payment_due_day !== null && (
                                <div>
                                    <dt className="text-[10px] uppercase tracking-wide text-slate-500">Payment Due</dt>
                                    <dd className="font-medium">{
                                        isNaN(Number(contract.payment_due_day))
                                            ? contract.payment_due_day.charAt(0).toUpperCase() + contract.payment_due_day.slice(1)
                                            : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][parseInt(contract.payment_due_day)] ?? contract.payment_due_day
                                    }</dd>
                                </div>
                            )}
                            {contract.funding_source && (
                                <div>
                                    <dt className="text-[10px] uppercase tracking-wide text-slate-500">Pay Via</dt>
                                    <dd className="font-medium text-violet-400">{contract.funding_source}</dd>
                                </div>
                            )}
                            {contract.admin_fee_amount && parseFloat(contract.admin_fee_amount) > 0 && (
                                <>
                                    <div>
                                        <dt className="text-[10px] uppercase tracking-wide text-slate-500">Admin Fee</dt>
                                        <dd className="font-medium">{formatCurrency(contract.admin_fee_amount)}</dd>
                                    </div>
                                    {contract.admin_fee_frequency && (
                                        <div>
                                            <dt className="text-[10px] uppercase tracking-wide text-slate-500">Fee Frequency</dt>
                                            <dd className="font-medium capitalize">{contract.admin_fee_frequency}</dd>
                                        </div>
                                    )}
                                </>
                            )}
                            {contract.min_principal_payoff_pct && parseFloat(contract.min_principal_payoff_pct) > 0 && (
                                <div>
                                    <dt className="text-[10px] uppercase tracking-wide text-slate-500">Min Principal Payoff</dt>
                                    <dd className="font-medium">{parseFloat(contract.min_principal_payoff_pct)}%</dd>
                                </div>
                            )}
                            <div>
                                <dt className="text-[10px] uppercase tracking-wide text-slate-500">Start Date</dt>
                                <dd className="font-medium">{contract.start_date ? formatDate(contract.start_date) : 'Upon signing'}</dd>
                            </div>
                        </dl>

                        <div className="mt-4 flex flex-wrap gap-2">
                            {contract.allow_adhoc_fees && <Badge variant="secondary" className="text-[10px]">Ad-hoc Fees</Badge>}
                            {contract.hardcore_mode && <Badge variant="destructive" className="text-[10px]">Hardcore</Badge>}
                            {contract.exclusive_dom && <Badge variant="outline" className="text-[10px] border-amber-400/30 text-amber-400">Exclusive</Badge>}
                        </div>
                    </CardContent>
                </Card>

                {/* Dom links & wishlists */}
                {contract.dom?.social_links && Object.keys(contract.dom.social_links).length > 0 && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">
                                {isDom ? 'Your Links' : `${contract.dom.display_name}'s Links`}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                {Object.entries(contract.dom.social_links).filter(([, v]) => v).map(([key, value]) => {
                                    const isUrl = typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'));
                                    const label = LINK_LABELS[key] ?? key;
                                    return isUrl ? (
                                        <a
                                            key={key}
                                            href={value}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 text-sm text-violet-400 hover:underline"
                                        >
                                            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                                            {label}
                                        </a>
                                    ) : (
                                        <div key={key} className="flex items-center gap-2 text-sm">
                                            <span className="text-slate-500">{label}:</span>
                                            <span className="text-slate-200">{value}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Record Payment + Quick Fees row */}
            {isDom && isActive && (
                <div className="grid gap-6 lg:grid-cols-2">
                    {/* Record payment */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Record Payment</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {paymentError && (
                                <div className="mb-3 rounded-md bg-red-900/30 p-2 text-sm text-red-400">
                                    {paymentError}
                                </div>
                            )}
                            <div className="flex gap-2">
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={contract.maximum_payment || undefined}
                                    placeholder={`Min ${formatCurrency(contract.minimum_payment)}`}
                                    value={paymentAmount}
                                    onChange={(e) => setPaymentAmount(e.target.value)}
                                />
                                <Button
                                    onClick={() => paymentMutation.mutate(parseFloat(paymentAmount))}
                                    disabled={!paymentAmount || paymentMutation.isPending}
                                >
                                    {paymentMutation.isPending ? 'Recording...' : 'Record'}
                                </Button>
                            </div>
                            <p className="mt-2 text-xs text-slate-500">
                                Min: {formatCurrency(contract.minimum_payment)}
                                {contract.maximum_payment && ` — Max: ${formatCurrency(contract.maximum_payment)}`}
                            </p>
                        </CardContent>
                    </Card>

                    {/* Predefined fee hot buttons */}
                    {contract.fee_types && contract.fee_types.filter((ft) => ft.is_active !== false && (!ft.fee_trigger || ft.fee_trigger === 'manual')).length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Quick Fees & Taxes</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {feeError && predefinedFeeMutation.isError && (
                                    <div className="mb-3 rounded-md bg-red-900/30 p-2 text-sm text-red-400">
                                        {feeError}
                                    </div>
                                )}
                                <div className="flex flex-wrap gap-2">
                                    {contract.fee_types
                                        .filter((ft) => ft.is_active !== false && (!ft.fee_trigger || ft.fee_trigger === 'manual'))
                                        .map((ft) => (
                                            <Button
                                                key={ft.id}
                                                variant={ft.category === 'tax' ? 'outline' : 'secondary'}
                                                size="sm"
                                                disabled={predefinedFeeMutation.isPending}
                                                onClick={() => predefinedFeeMutation.mutate(ft.id)}
                                            >
                                                {ft.name} — {formatCurrency(ft.default_amount)}
                                            </Button>
                                        ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Auto-triggered fees (display only) */}
            {isActive && contract.fee_types && contract.fee_types.filter((ft) => ft.is_active !== false && ft.fee_trigger && ft.fee_trigger !== 'manual').length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Auto Penalties</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {contract.fee_types
                                .filter((ft) => ft.is_active !== false && ft.fee_trigger && ft.fee_trigger !== 'manual')
                                .map((ft) => (
                                    <div key={ft.id} className="flex items-center justify-between rounded-md border border-slate-800 px-3 py-2">
                                        <div>
                                            <p className="text-sm font-medium">{ft.name}</p>
                                            <p className="text-[10px] text-slate-500">
                                                {ft.fee_trigger === 'missed_payment' && 'Triggered when a payment is missed'}
                                                {ft.fee_trigger === 'minimum_only' && 'Triggered when paying exactly the minimum'}
                                                {ft.fee_trigger === 'early_payoff' && 'Triggered when paying off too fast'}
                                            </p>
                                        </div>
                                        <span className="text-sm font-mono text-red-400">{formatCurrency(ft.default_amount)}</span>
                                    </div>
                                ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Contract Dates */}
            {(contract.trackers && contract.trackers.length > 0 || (isDom && isActive)) && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Contract Dates</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {contract.trackers && contract.trackers.length > 0 && (
                            <div className="space-y-2">
                                {contract.trackers.map((t) => (
                                    <div key={t.id} className="rounded-md border border-slate-800">
                                        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium truncate">{t.label}</p>
                                                {t.tracked_date ? (
                                                    <p className="text-[10px] text-slate-500">
                                                        {formatDate(t.tracked_date)} — <span className="text-violet-400">{t.days_since} day{t.days_since !== 1 ? 's' : ''} ago</span>
                                                        {t.occurrence_count != null && t.occurrence_count > 0 && (
                                                            <> — <span className="text-slate-400">{t.occurrence_count} time{t.occurrence_count !== 1 ? 's' : ''}</span></>
                                                        )}
                                                    </p>
                                                ) : (
                                                    <p className="text-[10px] text-slate-600">Not set</p>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-1 shrink-0">
                                                {isDom && isActive && editingTracker?.id === t.id ? (
                                                    <div className="flex flex-wrap items-center gap-1">
                                                        <input
                                                            type="date"
                                                            value={editingTracker.date}
                                                            onChange={(e) => setEditingTracker({ ...editingTracker, date: e.target.value })}
                                                            className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-100"
                                                        />
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            className="h-7 px-2 text-xs"
                                                            disabled={updateTrackerMutation.isPending}
                                                            onClick={() => updateTrackerMutation.mutate({ trackerId: t.id, tracked_date: editingTracker.date })}
                                                        >
                                                            Save
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 px-2 text-xs"
                                                            onClick={() => setEditingTracker(null)}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {isDom && t.tracked_date && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-7 px-2 text-xs text-slate-400"
                                                                onClick={() => setHistoryTrackerId(historyTrackerId === t.id ? null : t.id)}
                                                            >
                                                                History
                                                            </Button>
                                                        )}
                                                        {isDom && isActive && (
                                                            <>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-7 px-2 text-xs"
                                                                    onClick={() => updateTrackerMutation.mutate({ trackerId: t.id, tracked_date: new Date().toISOString().split('T')[0] })}
                                                                    disabled={updateTrackerMutation.isPending}
                                                                >
                                                                    Today
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-7 px-2 text-xs"
                                                                    onClick={() => setEditingTracker({ id: t.id, date: t.tracked_date || new Date().toISOString().split('T')[0] })}
                                                                >
                                                                    Pick
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-7 px-2 text-xs text-red-400 hover:text-red-300"
                                                                    onClick={() => deleteTrackerMutation.mutate(t.id)}
                                                                    disabled={deleteTrackerMutation.isPending}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {/* History panel */}
                                        {isDom && historyTrackerId === t.id && historyData?.data && (
                                            <div className="border-t border-slate-800 bg-slate-950/50 px-3 py-2">
                                                {historyData.data.length === 0 ? (
                                                    <p className="text-[10px] text-slate-600">No history yet.</p>
                                                ) : (
                                                    <div className="space-y-1">
                                                        {historyData.data.map((log) => (
                                                            <div key={log.id} className="flex items-center justify-between text-[10px]">
                                                                <span className="text-slate-500">
                                                                    {log.old_date ? formatDate(log.old_date) : '(none)'} → <span className="text-slate-300">{log.new_date ? formatDate(log.new_date) : '(cleared)'}</span>
                                                                </span>
                                                                <span className="text-slate-600">{formatDate(log.changed_at)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {isDom && isActive && (
                            <div className={`flex gap-2 ${contract.trackers && contract.trackers.length > 0 ? 'mt-3 border-t border-slate-800 pt-3' : ''}`}>
                                <Input
                                    placeholder="Label (e.g. Last Orgasm)"
                                    value={newTrackerLabel}
                                    onChange={(e) => setNewTrackerLabel(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && newTrackerLabel.trim()) {
                                            addTrackerMutation.mutate(newTrackerLabel.trim());
                                        }
                                    }}
                                />
                                <Button
                                    size="sm"
                                    onClick={() => addTrackerMutation.mutate(newTrackerLabel.trim())}
                                    disabled={!newTrackerLabel.trim() || addTrackerMutation.isPending}
                                >
                                    {addTrackerMutation.isPending ? 'Adding...' : 'Add'}
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Assess fee (dom only, active contract, adhoc fees enabled) */}
            {isDom && isActive && contract.allow_adhoc_fees && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Assess Fee / Tax</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {feeError && (
                            <div className="mb-3 rounded-md bg-red-900/30 p-2 text-sm text-red-400">
                                {feeError}
                            </div>
                        )}
                        <div className="space-y-3">
                            <Input
                                placeholder="Description (e.g. Loser Tax)"
                                value={feeForm.description}
                                onChange={(e) => setFeeForm((f) => ({ ...f, description: e.target.value }))}
                            />
                            <div className="flex gap-2">
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    placeholder="Amount"
                                    value={feeForm.amount}
                                    onChange={(e) => setFeeForm((f) => ({ ...f, amount: e.target.value }))}
                                />
                                <select
                                    value={feeForm.type}
                                    onChange={(e) => setFeeForm((f) => ({ ...f, type: e.target.value }))}
                                    className="rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100"
                                >
                                    <option value="fee">Fee</option>
                                    <option value="tax">Tax</option>
                                </select>
                            </div>
                            <Button
                                onClick={() => feeMutation.mutate({
                                    description: feeForm.description,
                                    amount: parseFloat(feeForm.amount),
                                    type: feeForm.type,
                                })}
                                disabled={!feeForm.description || !feeForm.amount || feeMutation.isPending}
                            >
                                {feeMutation.isPending ? 'Assessing...' : 'Assess'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Pillory */}
            {(isDom && isActive || (pilloryData?.data && pilloryData.data.length > 0)) && (
                <Card className="border-orange-900/30">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base text-orange-400">
                            <Target className="h-4 w-4" />
                            Pillory
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Active pillory posts */}
                        {pilloryData?.data && pilloryData.data.length > 0 && (
                            <div className="space-y-3">
                                {pilloryData.data.map((post) => {
                                    const diff = new Date(post.expires_at).getTime() - Date.now();
                                    const isExpired = diff <= 0;
                                    const hours = Math.floor(diff / 3600000);
                                    const minutes = Math.floor((diff % 3600000) / 60000);
                                    const timeLeft = isExpired ? 'Expired' : hours > 0 ? `${hours}h ${minutes}m left` : `${minutes}m left`;
                                    const canVote = post.is_active && !isDom && !isSub && (post.user_votes ?? 0) < post.max_votes_per_user;

                                    return (
                                        <div key={post.id} className="flex gap-3 rounded-md border border-orange-900/20 bg-orange-950/10 p-3">
                                            {contract.sub?.avatar_path ? (
                                                <img src={contract.sub.avatar_path} alt={contract.sub.display_name}
                                                    className="h-10 w-10 shrink-0 rounded-full object-cover" />
                                            ) : (
                                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-900/30 text-orange-400 font-bold">
                                                    {contract.sub?.display_name?.[0]?.toUpperCase() ?? '?'}
                                                </div>
                                            )}

                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 flex-wrap text-sm">
                                                    <ProfileLink userId={contract.sub!.id} displayName={contract.sub!.display_name}
                                                        className="font-semibold text-orange-400 hover:underline" />
                                                    <span className="text-slate-500">pilloried by</span>
                                                    <ProfileLink userId={contract.dom!.id} displayName={contract.dom!.display_name}
                                                        className="text-violet-400 hover:underline" />
                                                </div>

                                                <p className="mt-1 text-sm text-slate-300 italic">"{post.reason}"</p>

                                                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                                    <Badge variant="outline" className="gap-1 border-orange-800 text-orange-400">
                                                        <ThumbsUp className="h-3 w-3" />
                                                        {post.vote_count} vote{post.vote_count !== 1 ? 's' : ''}
                                                    </Badge>
                                                    <span className="text-slate-400">${parseFloat(post.fee_per_vote).toFixed(2)}/vote</span>
                                                    <span className="text-red-400 font-medium">${parseFloat(post.total_added).toFixed(2)} added</span>
                                                    <span className="flex items-center gap-1 text-slate-500">
                                                        <Clock className="h-3 w-3" />
                                                        {timeLeft}
                                                    </span>
                                                </div>

                                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                                    {canVote && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="border-orange-800 text-orange-400 hover:bg-orange-900/30"
                                                            disabled={pilloryVoteMutation.isPending}
                                                            onClick={() => pilloryVoteMutation.mutate(post.id)}
                                                        >
                                                            Vote (+${parseFloat(post.fee_per_vote).toFixed(2)})
                                                        </Button>
                                                    )}
                                                    {isExpired && <Badge variant="secondary" className="text-[10px]">Expired</Badge>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Post to pillory form (dom only) */}
                        {isDom && isActive && (
                            <div className="space-y-3 border-t border-slate-800 pt-3">
                                <p className="text-xs text-slate-500">Post this sub to the pillory for public punishment</p>
                                {pilloryError && (
                                    <div className="rounded-md bg-red-900/30 p-2 text-sm text-red-400">{pilloryError}</div>
                                )}
                                <Input
                                    placeholder="Reason (e.g. Late payment again...)"
                                    value={pilloryForm.reason}
                                    onChange={(e) => setPilloryForm((f) => ({ ...f, reason: e.target.value }))}
                                />
                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                                    <div>
                                        <label className="mb-1 block text-[10px] text-slate-500">$ Per Vote</label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            value={pilloryForm.fee_per_vote}
                                            onChange={(e) => setPilloryForm((f) => ({ ...f, fee_per_vote: e.target.value }))}
                                            placeholder="5.00"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-[10px] text-slate-500">Duration (hrs)</label>
                                        <Input
                                            type="number"
                                            min="1"
                                            max="168"
                                            value={pilloryForm.duration_hours}
                                            onChange={(e) => setPilloryForm((f) => ({ ...f, duration_hours: e.target.value }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-[10px] text-slate-500">Max Votes/User</label>
                                        <Input
                                            type="number"
                                            min="1"
                                            max="100"
                                            value={pilloryForm.max_votes_per_user}
                                            onChange={(e) => setPilloryForm((f) => ({ ...f, max_votes_per_user: e.target.value }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-[10px] text-slate-500">Max Total Votes</label>
                                        <Input
                                            type="number"
                                            min="1"
                                            max="10000"
                                            value={pilloryForm.max_total_votes}
                                            onChange={(e) => setPilloryForm((f) => ({ ...f, max_total_votes: e.target.value }))}
                                            placeholder="Optional"
                                        />
                                    </div>
                                </div>
                                <label className="flex items-center gap-2 text-xs text-slate-400">
                                    <input
                                        type="checkbox"
                                        checked={pilloryForm.require_login}
                                        onChange={(e) => setPilloryForm((f) => ({ ...f, require_login: e.target.checked }))}
                                        className="rounded border-slate-600"
                                    />
                                    Require login to vote
                                </label>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-orange-800 text-orange-400 hover:bg-orange-900/30"
                                    disabled={!pilloryForm.reason || !pilloryForm.fee_per_vote || pilloryMutation.isPending}
                                    onClick={() => pilloryMutation.mutate({
                                        contract_id: contract.id,
                                        reason: pilloryForm.reason,
                                        fee_per_vote: parseFloat(pilloryForm.fee_per_vote),
                                        duration_hours: parseInt(pilloryForm.duration_hours),
                                        max_votes_per_user: parseInt(pilloryForm.max_votes_per_user),
                                        require_login: pilloryForm.require_login,
                                        ...(pilloryForm.max_total_votes ? { max_total_votes: parseInt(pilloryForm.max_total_votes) } : {}),
                                    })}
                                >
                                    {pilloryMutation.isPending ? 'Posting...' : 'Post to Pillory'}
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Addons Section (active contracts with enabled addons, participants only) */}
            {isParticipant && (isActive || contract.status === 'pending') && (contract.enable_wheel || contract.enable_dice || contract.enable_tasks) && (
                <>
                    {contract.enable_wheel && <WheelOfFortune contractId={contract.id} isDom={isDom} />}
                    {contract.enable_dice && <DiceRoll contractId={contract.id} isDom={isDom} />}
                    {contract.enable_tasks && <ContractTasks contractId={contract.id} isDom={isDom} />}
                </>
            )}

            {/* Payment Skip */}
            {isParticipant && isActive && contract.allow_payment_skip && (
                <PaymentSkip contractId={contract.id} isDom={isDom} isSub={isSub} />
            )}

            {/* Share Links */}
            {isParticipant && isActive && (
                <ShareLinks contractId={contract.id} isDom={isDom} />
            )}

            {/* Review Section (closed/abandoned contracts only) */}
            {(contract.status === 'closed' || contract.status === 'abandoned') && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                            <Star className="h-4 w-4 text-amber-400" />
                            Review
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {reviewData?.data ? (
                            <div className="space-y-2">
                                <div className="flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <Star
                                            key={star}
                                            className={`h-4 w-4 ${star <= reviewData.data!.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`}
                                        />
                                    ))}
                                    <span className="ml-2 text-sm text-slate-400">
                                        by {reviewData.data.reviewer?.display_name}
                                    </span>
                                </div>
                                {reviewData.data.body && (
                                    <p className="text-sm text-slate-300">{reviewData.data.body}</p>
                                )}
                            </div>
                        ) : isSub ? (
                            <form
                                className="space-y-3"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    reviewMutation.mutate({
                                        contract_id: contract.id,
                                        rating: reviewForm.rating,
                                        body: reviewForm.body,
                                    });
                                }}
                            >
                                <div>
                                    <label className="text-xs text-slate-400 block mb-1">Rating</label>
                                    <div className="flex gap-1">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                type="button"
                                                onClick={() => setReviewForm((f) => ({ ...f, rating: star }))}
                                            >
                                                <Star
                                                    className={`h-6 w-6 cursor-pointer transition-colors ${star <= reviewForm.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-600 hover:text-amber-400'}`}
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400 block mb-1">Review (optional)</label>
                                    <textarea
                                        className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 placeholder-slate-500"
                                        rows={3}
                                        placeholder="Share your experience..."
                                        value={reviewForm.body}
                                        onChange={(e) => setReviewForm((f) => ({ ...f, body: e.target.value }))}
                                        maxLength={2000}
                                    />
                                </div>
                                {reviewError && <p className="text-xs text-red-400">{reviewError}</p>}
                                <Button type="submit" size="sm" disabled={reviewMutation.isPending}>
                                    {reviewMutation.isPending ? 'Submitting...' : 'Submit Review'}
                                </Button>
                            </form>
                        ) : (
                            <p className="text-sm text-slate-500 italic">No review yet.</p>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Balance Chart */}
            {entries.length >= 2 && (
                <Card>
                    <CardContent className="p-4">
                        <BalanceChart entries={entries} />
                    </CardContent>
                </Card>
            )}

            {/* Payment History Timeline */}
            <div>
                <h2 className="mb-3 text-lg font-semibold">Payment History</h2>
                {entries.length === 0 ? (
                    <p className="text-sm text-slate-400">No entries yet.</p>
                ) : (
                    <PaymentTimeline
                        entries={entries}
                        contractId={contract.id}
                        isDom={isDom}
                    />
                )}
            </div>
        </div>
    );
}
